/**
 * Logique pure du formulaire de notification d'achat (post-conversion privée + preuve sociale
 * publique) : conversion entre le template texte stocké côté back ("Bravo [Prénom] !") et les
 * chunks édités dans l'UI, validation de contenu, et résolution des couleurs entre les presets de
 * l'identité visuelle du workspace et les valeurs hex envoyées à l'API.
 */

import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type {
  WebinarPurchaseNotificationConfig,
  WebinarPurchaseNotificationConfigInput,
} from '@/features/live/types';

export type NotifChunk = { type: 'text' | 'field'; value: string; id: string };

export const hasValidContent = (chunks: NotifChunk[]): boolean => {
  const nonEmptyChunks = chunks.filter(
    (chunk) => chunk.type === 'field' || (chunk.type === 'text' && chunk.value.trim() !== ''),
  );
  return nonEmptyChunks.length > 0;
};

export const messageTemplateToChunks = (template: string | null | undefined): NotifChunk[] => {
  const chunks: NotifChunk[] = [];

  if (!template) {
    return [{ type: 'text', value: '', id: crypto.randomUUID() }];
  }

  const placeholderRegex = /\[([^\]]+)\]/g;
  const matches = Array.from(template.matchAll(placeholderRegex));

  if (matches.length === 0) {
    return [{ type: 'text', value: template, id: crypto.randomUUID() }];
  }

  let lastIndex = 0;

  matches.forEach((match, index) => {
    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;
    const placeholder = match[0];

    const textBefore = template.substring(lastIndex, matchStart);

    if (index === 0 && matchStart === 0) {
      chunks.push({ type: 'text', value: '', id: crypto.randomUUID() });
    } else if (textBefore.trim() || textBefore) {
      chunks.push({ type: 'text', value: textBefore, id: crypto.randomUUID() });
    } else if (index > 0) {
      chunks.push({ type: 'text', value: ' ', id: crypto.randomUUID() });
    }

    chunks.push({ type: 'field', value: placeholder, id: crypto.randomUUID() });

    lastIndex = matchEnd;
  });

  const textAfter = template.substring(lastIndex);
  if (textAfter) {
    chunks.push({ type: 'text', value: textAfter, id: crypto.randomUUID() });
  } else if (matches.length > 0 && lastIndex === template.length) {
    chunks.push({ type: 'text', value: '', id: crypto.randomUUID() });
  }

  return chunks;
};

export const chunksToMessageTemplate = (chunks: NotifChunk[]): string => {
  const filtered = chunks.filter(
    (c) => c.type === 'field' || (c.type === 'text' && c.value.trim() !== ''),
  );
  let result = filtered
    .map((chunk, index) => {
      if (chunk.type !== 'field') return chunk.value;
      const prev = index > 0 ? filtered[index - 1] : null;
      const next = index < filtered.length - 1 ? filtered[index + 1] : null;
      const prevEndsWithSpace = prev?.type === 'text' && prev.value.endsWith(' ');
      const prevIsField = prev?.type === 'field';
      const nextStartsWithSpace = next?.type === 'text' && next.value.startsWith(' ');
      const nextIsField = next?.type === 'field';
      const nextIsPunct =
        next?.type === 'text' &&
        next.value.trimStart() !== '' &&
        /^[.,!?;:)]/.test(next.value.trimStart());
      let out = chunk.value;
      if (!prevIsField && !prevEndsWithSpace) out = ' ' + out;
      if (!nextIsPunct && !nextStartsWithSpace && !nextIsField) out = out + ' ';
      else if (nextIsField) out = out + ' ';
      return out;
    })
    .join('');
  result = result.replace(/\s+([.,!?;:)]+)/g, '$1').trim();
  return result;
};

export const calculateMessageLength = (chunks: NotifChunk[]): number =>
  chunksToMessageTemplate(chunks).length;

export const isCustomColor = (color: string | undefined): boolean => {
  if (!color) return false;
  return /^#[0-9A-Fa-f]{6}$/.test(color);
};

/** Retrouve l'identifiant de preset (accent/background/...) correspondant à une couleur hex enregistrée. */
export const mapColorToId = (
  colorValue: string | null | undefined,
  defaultColorId: string,
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): string => {
  if (!colorValue) {
    return defaultColorId;
  }

  if (colorValue === 'gradient1' || colorValue === 'gradient-1') {
    return 'gradient1-color1';
  }
  if (colorValue === 'gradient2' || colorValue === 'gradient-2') {
    return 'gradient2-color1';
  }

  if (!isCustomColor(colorValue)) {
    return colorValue;
  }

  const normalizedColor = colorValue.toUpperCase();
  if (visualIdentity) {
    if (
      visualIdentity.accentColor &&
      normalizedColor === visualIdentity.accentColor.toUpperCase()
    ) {
      return 'accent';
    }
    if (
      visualIdentity.backgroundColor &&
      normalizedColor === visualIdentity.backgroundColor.toUpperCase()
    ) {
      return 'background';
    }
    if (
      visualIdentity.contrastColor &&
      normalizedColor === visualIdentity.contrastColor.toUpperCase()
    ) {
      return 'contrast';
    }
    if (
      visualIdentity.secondaryBackgroundColor &&
      normalizedColor === visualIdentity.secondaryBackgroundColor.toUpperCase()
    ) {
      return 'secondary-background';
    }
    if (
      visualIdentity.gradient1Color1 &&
      normalizedColor === visualIdentity.gradient1Color1.toUpperCase()
    ) {
      return 'gradient1-color1';
    }
    if (
      visualIdentity.gradient2Color1 &&
      normalizedColor === visualIdentity.gradient2Color1.toUpperCase()
    ) {
      return 'gradient2-color1';
    }
  }

  if (normalizedColor === '#000000') {
    return 'black';
  }
  if (normalizedColor === '#FFFFFF') {
    return 'white';
  }

  return 'custom';
};

export const isNoBorder = (borderColor: string | null | undefined): boolean =>
  !borderColor || borderColor === 'none' || borderColor.toLowerCase() === 'transparent';

/** Résout un identifiant de preset (ou une couleur custom) vers sa valeur hex affichable. */
export const resolveColorToHex = (
  presetId: string,
  customHex: string | undefined,
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): string => {
  if (presetId === 'custom' && customHex) return customHex;
  if (presetId === 'no-border') return 'transparent';
  const map: Record<string, string | null | undefined> = {
    accent: visualIdentity?.accentColor,
    background: visualIdentity?.backgroundColor,
    contrast: visualIdentity?.contrastColor,
    'gradient1-color1': visualIdentity?.gradient1Color1,
    'gradient2-color1': visualIdentity?.gradient2Color1,
    'gradient1-color2': visualIdentity?.gradient1Color1,
    'gradient2-color2': visualIdentity?.gradient2Color1,
    'secondary-background': visualIdentity?.secondaryBackgroundColor,
    black: '#000000',
    white: '#FFFFFF',
  };
  return map[presetId] || customHex || '#FFFFFF';
};

/** Résout la couleur de fond pour l'API : envoie "gradient-1" / "gradient-2" au lieu du hex. */
export const resolveBackgroundColorForApi = (
  presetId: string,
  customHex: string | undefined,
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): string => {
  if (presetId === 'gradient1-color1' || presetId === 'gradient1-color2') return 'gradient-1';
  if (presetId === 'gradient2-color1' || presetId === 'gradient2-color2') return 'gradient-2';
  return resolveColorToHex(presetId, customHex, visualIdentity);
};

export const buildDisabledPurchaseNotificationInput = (
  config: WebinarPurchaseNotificationConfig | null | undefined,
): WebinarPurchaseNotificationConfigInput => ({
  privateMessageTemplate: config?.privateMessageTemplate ?? '',
  privateIcon: config?.privateIcon ?? '',
  displayPrivateIcon: config?.displayPrivateIcon ?? true,
  privateBackgroundColor: config?.privateBackgroundColor ?? '',
  privateBorderColor: config?.privateBorderColor ?? '',
  privateTextColor: config?.privateTextColor ?? '',
  privateBackgroundIcon: config?.privateBackgroundIcon ?? '',
  privateIconColor: config?.privateIconColor ?? '',
  publicIcon: config?.publicIcon ?? '',
  displayPublicIcon: config?.displayPublicIcon ?? true,
  publicBackgroundColor: config?.publicBackgroundColor ?? '',
  publicBorderColor: config?.publicBorderColor ?? '',
  publicTextColor: config?.publicTextColor ?? '',
  publicBackgroundIcon: config?.publicBackgroundIcon ?? '',
  publicIconColor: config?.publicIconColor ?? '',
  publicVariants: (config?.publicVariants ?? []).map((variant, index) => ({
    messageTemplate: variant.messageTemplate,
    position: variant.position ?? index,
  })),
  enabled: false,
});

export const resolveNotificationSwitchState = (
  templateId: number | null | undefined,
  config: WebinarPurchaseNotificationConfig | null | undefined,
): boolean => {
  if (templateId == null) return true;
  return config?.enabled === true;
};

/**
 * Le drapeau de chargement ne retombe à false que sur une réponse (config reçue ou 4xx) : tant qu'il
 * vaut undefined ou true, on n'a pas encore de quoi distinguer « pas de config » de « pas encore reçue ».
 */
export const isNotificationConfigReady = (
  templateId: number | null | undefined,
  loading: boolean | undefined,
): boolean => {
  if (templateId == null) return true;
  return loading === false;
};
